import DynamicSelectInput, { DynamicSelectInputProps } from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import { useEffect } from 'react';
import { FieldValues } from 'react-hook-form';
import { useIsOneDriveIntegrationEnabledQuery, useLazyGetDmsTeamsQuery } from 'store/api/dmsTeam';
import type { DmsTeam } from 'types/dmsTeam';

type DmsTeamSelectProps<TFormData extends FieldValues> = Omit<
    DynamicSelectInputProps<TFormData, DmsTeam>,
    'paginated' | 'getOptionLabel' | 'getOptionIdentifier'
>;

/**
 * Whether the select draws: only once OneDrive is known to be on, so not while the answer loads and not when it
 * fails or says no. A read view that lists the field asks too, to know whether its row has a field to edit it.
 */
export const useDmsTeamSelectState = (options?: { skip?: boolean }) => {
    const query = useIsOneDriveIntegrationEnabledQuery(undefined, options);
    return { ...query, isShown: !query.isLoading && !!query.data };
};

const DmsTeamSelect = <TFormData extends FieldValues>(props: DmsTeamSelectProps<TFormData>) => {
    const { isShown, isError, error } = useDmsTeamSelectState();

    // TODO(react-doctor: no-effect-event-handler): logs an async RTK Query
    // error — reacting to fetched data, not to a user event.
    // Log error to console if OneDrive integration check fails
    useEffect(() => {
        if (isError) {
            console.error('Failed to check OneDrive integration status:', error);
        }
    }, [isError, error]);

    if (!isShown) {
        return null;
    }

    return (
        <DynamicSelectInput<TFormData, DmsTeam>
            {...props}
            label="teams.azure-ad-group"
            placeholder="teams.select-dms-team"
            paginated={{
                fetch: useLazyGetDmsTeamsQuery,
                searchField: 'search',
                useCustomSearch: true,
                isEnabled: true,
            }}
            getOptionLabel="displayName"
            getOptionIdentifier="id"
            clearable
            readonly={props.readonly}
        />
    );
};

export default DmsTeamSelect;
